import {useEffect, useMemo} from "react";

import {selectSegmentBuildingTypeRentedIndicationPayload} from "../redux/indicationSelectors";
import {selectRevaId} from "../redux/persistenceSlice";
import {useRevaDispatch, useRevaSelector} from "../redux/revaHooks";
import {
    selectValueOrderEntitlementsData,
    selectValueOrderEntitlementsState,
} from "../redux/valueOrderEntitlementsSlice";
import {loadValueOrderEntitlementsThunk} from "../redux/valueOrderEntitlementsThunk";
import {
    type CommissionEntitlementsEvaluation,
    evaluateCommissionEntitlements,
    isRevaOrderTypeNotEnabled,
} from "../util/valueOrderEntitlements";
import {segmentBuildingTypeToPropertyType} from "../util/valueOrderPropertyType";

/**
 * Lädt die Freischaltungen beim ValueAG-Auftraggeber der Lizenz des angemeldeten Benutzers (mit dieser Lizenz
 * beauftragt reva-rest) und bewertet sie für die Gebäudeart der geladenen Bewertung. Abgelaufene Antworten werden
 * beim Öffnen einer Bewertung neu geladen. Solange nichts geladen ist, lautet das Ergebnis `unrestricted`.
 * @param enabled false unterdrückt Laden und Einschränkung, z. B. wenn das Beauftragen ohnehin nicht angeboten wird
 */
export function useCommissionEntitlements(enabled: boolean): CommissionEntitlementsEvaluation {
    const dispatch = useRevaDispatch();
    const revaId = useRevaSelector(selectRevaId);
    const entitlements = useRevaSelector(selectValueOrderEntitlementsData);
    const segmentPayload = useRevaSelector(selectSegmentBuildingTypeRentedIndicationPayload);
    const propertyType = segmentBuildingTypeToPropertyType(segmentPayload?.segment, segmentPayload?.building_type);

    useEffect(() => {
        if (enabled) {
            void dispatch(loadValueOrderEntitlementsThunk());
        }
    }, [dispatch, enabled, revaId]);

    const data = enabled ? entitlements : null;
    return useMemo(() => evaluateCommissionEntitlements(data, propertyType), [data, propertyType]);
}

export interface RevaOrderTypeLock {
    /** Die erste Abfrage nach dem Login läuft noch – so lange weder App noch Fehlerseite zeigen. */
    pending: boolean;
    /** Beim Auftraggeber der eigenen Lizenz ist die Auftragsart REVA nachweislich nicht freigeschaltet. */
    notEnabled: boolean;
}

/**
 * Ob die Auftragsart REVA beim ValueAG-Auftraggeber der eigenen Lizenz fehlt (siehe
 * {@link isRevaOrderTypeNotEnabled}); geladen wird direkt nach dem Login (`loginResponseFlow`). Nur die erste Abfrage
 * zählt als `pending`: späteres Neuladen behält die letzte Antwort, damit nichts flackert. Wurde nie abgefragt
 * (z. B. ohne Credentials), ist nichts `pending`.
 */
export function useRevaOrderTypeLock(): RevaOrderTypeLock {
    const {loading, loadedAt, data} = useRevaSelector(selectValueOrderEntitlementsState);
    return {pending: loading && loadedAt === null, notEnabled: isRevaOrderTypeNotEnabled(data)};
}
